import { newId } from './idGen';

/**
 * Circle method round robin: participant 0 stays fixed, everyone else rotates
 * around it each round, guaranteeing every pair meets exactly once per leg.
 * Works for any n >= 2 (an odd n gets a synthetic BYE seat that is dropped
 * from the output, giving that participant a free round).
 *
 * `legs` (default 1) repeats the whole cycle that many times — e.g. legs: 2
 * for a "home and away" double round robin (Page Playoff's league stage
 * defaults to this). Each repeat swaps slotA/slotB so the second meeting
 * reads as the reverse fixture, not a literal duplicate. Round/match `number`
 * stays sequential across every leg, not reset per leg — but `id` (see
 * idGen.js) is a GUID, not derived from that number, since group_knockout and
 * ladder both call this function once PER group/court and then, in
 * group_knockout's case, merge every group's matches into one shared list —
 * a positional id would collide there where a GUID can't.
 */
export const generateRoundRobin = (participants, options = {}) => {
  const n = participants.length;
  if (n < 2) {
    throw new Error('Round Robin needs at least 2 participants');
  }
  const legs = Math.max(1, options.legs || 1);

  const seats = [...participants];
  if (seats.length % 2 !== 0) seats.push(null); // BYE seat

  const total = seats.length;
  const half = total / 2;
  const roundsPerLeg = total - 1;
  const rounds = [];
  let matchCounter = 1; // sequential across ALL rounds/legs, never reset

  for (let leg = 1; leg <= legs; leg++) {
    let arr = [...seats];
    for (let r = 0; r < roundsPerLeg; r++) {
      const matches = [];
      for (let i = 0; i < half; i++) {
        const a = arr[i];
        const b = arr[total - 1 - i];
        if (a && b) {
          const [slotA, slotB] = leg % 2 === 1 ? [a, b] : [b, a];
          matches.push({
            id: newId(),
            number: matchCounter,
            round: (leg - 1) * roundsPerLeg + r + 1,
            slotA,
            slotB,
          });
          matchCounter += 1;
        }
      }
      const roundNumber = (leg - 1) * roundsPerLeg + r + 1;
      const label = legs > 1 ? `Leg ${leg} · Round ${r + 1}` : `Round ${r + 1}`;
      rounds.push({ id: newId(), round: roundNumber, label, matches });
      arr = [arr[0], arr[total - 1], ...arr.slice(1, total - 1)];
    }
  }

  return rounds;
};

export const generateRoundRobinFormat = (participants, options = {}) => {
  const rounds = generateRoundRobin(participants, { legs: options.legs });
  return {
    formatId: 'round_robin',
    participantsCount: participants.length,
    totalMatches: rounds.reduce((sum, r) => sum + r.matches.length, 0),
    rounds,
  };
};
